import React from "react";
import {
    Button,
    DatePicker,
    Empty,
    InputNumber,
    Radio,
    Select,
    Space,
    Switch,
    Tag,
    Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useIntl } from "react-intl";
import {
    ISegmentFilterCriterion,
    ISegmentationField,
    MatchLogic,
    SegmentationFieldType,
    SegmentOperator,
} from "../../services/nestjs-base-api";

const { Text } = Typography;
const { Option } = Select;

interface Props {
    fields: ISegmentationField[];
    criteria: ISegmentFilterCriterion[];
    matchLogic: MatchLogic;
    onCriteriaChange: (criteria: ISegmentFilterCriterion[]) => void;
    onMatchLogicChange: (logic: MatchLogic) => void;
}

const fieldTypeColors: Record<SegmentationFieldType, string> = {
    text: "blue",
    select: "green",
    number: "purple",
    date: "gold",
    boolean: "cyan",
    multi_select: "magenta",
    currency: "orange",
};

const operatorsByType: Record<SegmentationFieldType, SegmentOperator[]> = {
    text: ["eq", "neq", "in", "not_in", "contains", "starts_with", "is_set", "is_unset"],
    select: ["eq", "neq", "in", "not_in", "is_set", "is_unset"],
    number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "in", "not_in", "is_set", "is_unset"],
    date: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "is_set", "is_unset"],
    boolean: ["eq", "is_set", "is_unset"],
    multi_select: ["contains", "in", "not_in", "is_set", "is_unset"],
    currency: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "is_set", "is_unset"],
};

const operatorLabels: Record<SegmentOperator, { id: string; defaultMessage: string }> = {
    eq: { id: "segments.op.eq", defaultMessage: "is" },
    neq: { id: "segments.op.neq", defaultMessage: "is not" },
    in: { id: "segments.op.in", defaultMessage: "is one of" },
    not_in: { id: "segments.op.not_in", defaultMessage: "is not one of" },
    gt: { id: "segments.op.gt", defaultMessage: ">" },
    gte: { id: "segments.op.gte", defaultMessage: "≥" },
    lt: { id: "segments.op.lt", defaultMessage: "<" },
    lte: { id: "segments.op.lte", defaultMessage: "≤" },
    between: { id: "segments.op.between", defaultMessage: "between" },
    contains: { id: "segments.op.contains", defaultMessage: "contains" },
    starts_with: { id: "segments.op.starts_with", defaultMessage: "starts with" },
    is_set: { id: "segments.op.is_set", defaultMessage: "is set" },
    is_unset: { id: "segments.op.is_unset", defaultMessage: "is not set" },
};

const VALUELESS_OPERATORS = new Set<SegmentOperator>(["is_set", "is_unset"]);

export const SegmentBuilder: React.FC<Props> = ({
    fields,
    criteria,
    matchLogic,
    onCriteriaChange,
    onMatchLogicChange,
}) => {
    const intl = useIntl();
    const fieldsById = React.useMemo(
        () => new Map(fields.map((f) => [f.id, f])),
        [fields],
    );

    const handleAdd = () => {
        const firstField = fields[0];
        if (!firstField) return;
        const operator = operatorsByType[firstField.fieldType][0];
        onCriteriaChange([
            ...criteria,
            { fieldId: firstField.id, operator, value: undefined },
        ]);
    };

    const handleUpdate = (
        index: number,
        patch: Partial<ISegmentFilterCriterion>,
    ) => {
        const next = [...criteria];
        next[index] = { ...next[index], ...patch };
        onCriteriaChange(next);
    };

    const handleRemove = (index: number) => {
        onCriteriaChange(criteria.filter((_, i) => i !== index));
    };

    if (fields.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({
                    id: "segments.builder.noFields",
                    defaultMessage: "Create segmentation fields first to build a segment",
                })}
            />
        );
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Space>
                <Text>{intl.formatMessage({
                    id: "segments.builder.matchLogicLabel",
                    defaultMessage: "Match",
                })}</Text>
                <Radio.Group
                    value={matchLogic}
                    onChange={(e) => onMatchLogicChange(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    options={[
                        { value: "all", label: intl.formatMessage({ id: "segments.builder.matchAll", defaultMessage: "ALL of" }) },
                        { value: "any", label: intl.formatMessage({ id: "segments.builder.matchAny", defaultMessage: "ANY of" }) },
                    ]}
                />
                <Text>{intl.formatMessage({
                    id: "segments.builder.matchLogicSuffix",
                    defaultMessage: "the criteria below",
                })}</Text>
            </Space>

            {criteria.map((c, idx) => (
                <CriterionRow
                    key={idx}
                    fields={fields}
                    fieldsById={fieldsById}
                    criterion={c}
                    onUpdate={(patch) => handleUpdate(idx, patch)}
                    onRemove={() => handleRemove(idx)}
                />
            ))}

            <Button icon={<PlusOutlined />} onClick={handleAdd} type="dashed">
                {intl.formatMessage({
                    id: "segments.builder.addCriterion",
                    defaultMessage: "Add criterion",
                })}
            </Button>
        </Space>
    );
};

interface CriterionRowProps {
    fields: ISegmentationField[];
    fieldsById: Map<number, ISegmentationField>;
    criterion: ISegmentFilterCriterion;
    onUpdate: (patch: Partial<ISegmentFilterCriterion>) => void;
    onRemove: () => void;
}

const CriterionRow: React.FC<CriterionRowProps> = ({
    fields,
    fieldsById,
    criterion,
    onUpdate,
    onRemove,
}) => {
    const intl = useIntl();
    const field = fieldsById.get(criterion.fieldId);
    if (!field) {
        return (
            <Space>
                <Tag color="red">missing field</Tag>
                <Button icon={<DeleteOutlined />} size="small" danger onClick={onRemove} />
            </Space>
        );
    }
    const ops = operatorsByType[field.fieldType];
    const showValueEditor = !VALUELESS_OPERATORS.has(criterion.operator);

    return (
        <Space wrap align="start">
            <Select
                style={{ width: 220 }}
                value={field.id}
                onChange={(newFieldId) => {
                    const newField = fieldsById.get(newFieldId);
                    if (!newField) return;
                    const newOps = operatorsByType[newField.fieldType];
                    const operator = newOps.includes(criterion.operator)
                        ? criterion.operator
                        : newOps[0];
                    onUpdate({ fieldId: newFieldId, operator, value: undefined });
                }}
                options={fields.map((f) => ({
                    value: f.id,
                    label: (
                        <Space size={4}>
                            <span>{f.fieldLabel}</span>
                            <Tag color={fieldTypeColors[f.fieldType]}>{f.fieldType}</Tag>
                        </Space>
                    ),
                }))}
            />
            <Select
                style={{ width: 140 }}
                value={criterion.operator}
                onChange={(operator) => onUpdate({ operator, value: undefined })}
                options={ops.map((op) => ({
                    value: op,
                    label: intl.formatMessage(operatorLabels[op]),
                }))}
            />
            {showValueEditor && (
                <ValueEditor
                    field={field}
                    operator={criterion.operator}
                    value={criterion.value}
                    onChange={(value) => onUpdate({ value })}
                />
            )}
            <Button icon={<DeleteOutlined />} size="small" danger onClick={onRemove} />
        </Space>
    );
};

const ValueEditor: React.FC<{
    field: ISegmentationField;
    operator: SegmentOperator;
    value: unknown;
    onChange: (value: unknown) => void;
}> = ({ field, operator, value, onChange }) => {
    const isMultiValue =
        operator === "in" ||
        operator === "not_in" ||
        operator === "between";

    if (operator === "between") {
        const arr = (Array.isArray(value) ? value : [undefined, undefined]) as [
            unknown,
            unknown,
        ];
        if (field.fieldType === "date") {
            const dayjsRange: [Dayjs | null, Dayjs | null] = [
                arr[0] ? dayjs(arr[0] as string) : null,
                arr[1] ? dayjs(arr[1] as string) : null,
            ];
            return (
                <DatePicker.RangePicker
                    style={{ width: 280 }}
                    value={dayjsRange}
                    onChange={(range) =>
                        onChange([
                            range?.[0]?.format("YYYY-MM-DD") ?? null,
                            range?.[1]?.format("YYYY-MM-DD") ?? null,
                        ])
                    }
                />
            );
        }
        return (
            <Space>
                <InputNumber
                    placeholder="min"
                    value={arr[0] as number}
                    onChange={(v) => onChange([v, arr[1]])}
                />
                <InputNumber
                    placeholder="max"
                    value={arr[1] as number}
                    onChange={(v) => onChange([arr[0], v])}
                />
            </Space>
        );
    }

    switch (field.fieldType) {
        case "boolean":
            return (
                <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    checked={value === true || value === "true" || value === 1}
                    onChange={onChange}
                />
            );
        case "date":
            return (
                <DatePicker
                    value={value ? dayjs(value as string) : null}
                    onChange={(d) => onChange(d ? d.format("YYYY-MM-DD") : null)}
                />
            );
        case "select":
            if (isMultiValue) {
                return (
                    <Select
                        mode="multiple"
                        style={{ minWidth: 200 }}
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                    >
                        {(field.options ?? []).map((opt) => (
                            <Option key={opt} value={opt}>{opt}</Option>
                        ))}
                    </Select>
                );
            }
            return (
                <Select
                    style={{ minWidth: 160 }}
                    value={(value as string) ?? undefined}
                    onChange={onChange}
                    allowClear
                >
                    {(field.options ?? []).map((opt) => (
                        <Option key={opt} value={opt}>{opt}</Option>
                    ))}
                </Select>
            );
        case "multi_select":
            if (operator === "contains") {
                return (
                    <Select
                        style={{ minWidth: 160 }}
                        value={(value as string) ?? undefined}
                        onChange={onChange}
                        allowClear
                    >
                        {(field.options ?? []).map((opt) => (
                            <Option key={opt} value={opt}>{opt}</Option>
                        ))}
                    </Select>
                );
            }
            return (
                <Select
                    mode="multiple"
                    style={{ minWidth: 200 }}
                    value={(value as string[]) ?? []}
                    onChange={onChange}
                >
                    {(field.options ?? []).map((opt) => (
                        <Option key={opt} value={opt}>{opt}</Option>
                    ))}
                </Select>
            );
        case "number":
            if (isMultiValue) {
                return (
                    <Select
                        mode="tags"
                        style={{ minWidth: 200 }}
                        value={((value as unknown[]) ?? []).map(String)}
                        onChange={(arr) => onChange(arr.map(Number))}
                        tokenSeparators={[",", " "]}
                    />
                );
            }
            return (
                <InputNumber value={value as number} onChange={onChange} />
            );
        case "currency":
            if (isMultiValue) {
                return (
                    <Select
                        mode="tags"
                        style={{ minWidth: 200 }}
                        value={((value as unknown[]) ?? []).map(String)}
                        onChange={(arr) => onChange(arr.map(Number))}
                        tokenSeparators={[",", " "]}
                    />
                );
            }
            return (
                <InputNumber
                    addonAfter={field.fieldMetadata?.currencyCode ?? "USD"}
                    value={value as number}
                    onChange={onChange}
                    step={0.01}
                />
            );
        case "text":
        default:
            if (isMultiValue) {
                return (
                    <Select
                        mode="tags"
                        style={{ minWidth: 200 }}
                        value={((value as unknown[]) ?? []).map(String)}
                        onChange={onChange}
                        tokenSeparators={[",", " "]}
                    />
                );
            }
            return (
                <input
                    style={{ padding: "4px 11px", border: "1px solid #d9d9d9", borderRadius: 6 }}
                    value={(value as string) ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
    }
};
